"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { usePlannerEvent } from "@/contexts/PlannerEventContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus,
  Trash2,
  Edit,
  X,
  Save,
  Download,
  Search,
  GripVertical,
  ListOrdered,
} from "lucide-react";
import { Reorder } from "framer-motion";
import {
  getGuestLists,
  createGuestList,
  updateGuestList,
  deleteGuestList,
  type GuestList,
  type Guest,
} from "@/lib/api";
import {
  fixSerbianText,
  drawPDFHeader,
  drawPDFFooter,
  drawSectionLabel,
  drawStatRow,
  getPDFTableStyles,
  loadPDFLogo,
  loadPDF,
  drawPDFTableCheckbox,
  C,
  MARGIN,
  maxContentY,
} from "@/lib/pdf-utils";
import { PlannerEventRequiredNotice } from "@/components/PlannerEventRequiredNotice";
import { EventDataLoading } from "@/components/EventDataLoading";
import {
  isUnassignedGuestList,
  pinUnassignedGuestListLast,
  UNASSIGNED_GUEST_LIST_NAME,
} from "@/lib/guest-list-constants";
import { normalizeForSearch, searchIncludes } from "@/lib/normalize-search";
import { toast } from "sonner";

export default function SpisakGostijuPage() {
  const { user, token, isLoading: authLoading } = useAuth();
  const {
    activeEventId,
    activeEventNumericId,
    listReady: plannerListReady,
    summaries: plannerSummaries,
  } = usePlannerEvent();
  const hasNoEvents = plannerListReady && plannerSummaries.length === 0;
  const activeEventNumericIdRef = useRef(activeEventNumericId);
  useEffect(() => {
    activeEventNumericIdRef.current = activeEventNumericId;
  }, [activeEventNumericId]);

  const router = useRouter();
  const [guestLists, setGuestLists] = useState<GuestList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateGuestDialogOpen, setIsCreateGuestDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState<GuestList | null>(null);
  const [deleteListId, setDeleteListId] = useState<number | null>(null);

  // Form state
  const [listName, setListName] = useState("");
  const [listNameError, setListNameError] = useState<string | null>(null);
  const [listDescription, setListDescription] = useState("");
  const [guests, setGuests] = useState<Guest[]>([]);
  // Single guest form state
  const [currentGuest, setCurrentGuest] = useState<Guest>({
    name: "",
    phone: "",
    notes: "",
    status: "Na čekanju",
  });
  const [currentGuestFirstName, setCurrentGuestFirstName] = useState("");
  const [currentGuestLastName, setCurrentGuestLastName] = useState("");
  // Editing guest state
  const [editingGuestIndex, setEditingGuestIndex] = useState<number | null>(
    null
  );
  const [isEditGuestDialogOpen, setIsEditGuestDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [deletingGuest, setDeletingGuest] = useState<{
    listId: number;
    guestIndex: number;
    guestName: string;
  } | null>(null);
  const [editingGuestFirstName, setEditingGuestFirstName] = useState("");
  const [editingGuestLastName, setEditingGuestLastName] = useState("");
  const [editingGuestTargetListId, setEditingGuestTargetListId] = useState<
    number | null
  >(null);
  const [newGuestListId, setNewGuestListId] = useState<string>("");
  const [newGuestFirstName, setNewGuestFirstName] = useState("");
  const [newGuestLastName, setNewGuestLastName] = useState("");
  const [newGuest, setNewGuest] = useState<Guest>({
    name: "",
    phone: "",
    notes: "",
    status: "Na čekanju",
  });

  // Search state
  const [guestSearchQuery, setGuestSearchQuery] = useState("");
  const [guestStatusFilter, setGuestStatusFilter] = useState<string>("Svi");

  // Accordion state - track which lists are open
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  // Accordion state for "Svi gosti" - track if it's open
  const [isAllGuestsAccordionOpen, setIsAllGuestsAccordionOpen] = useState<
    string | undefined
  >(undefined);

  // Reorder lists dialog
  const [isReorderDialogOpen, setIsReorderDialogOpen] = useState(false);

  // List search and sort (for display only; guestLists order is preserved)
  const [listSearchQuery, setListSearchQuery] = useState("");
  const [listSortBy, setListSortBy] = useState<string>("custom");

  const displayLists = useMemo(() => {
    const query = normalizeForSearch(listSearchQuery);
    let lists = guestLists.filter((list) => !isUnassignedGuestList(list));
    if (query) {
      lists = lists.filter((list) => {
        const name = (list.attributes?.name ?? list.name ?? "")
          .toString()
          .trim();
        return searchIncludes(name, query);
      });
    }
    const getCreatedAt = (list: GuestList) =>
      list.attributes?.createdAt ?? list.createdAt ?? "";
    const getUpdatedAt = (list: GuestList) =>
      list.attributes?.updatedAt ?? list.updatedAt ?? "";
    const getGuestCount = (list: GuestList) => {
      const g =
        list.attributes?.guest ??
        list.attributes?.guests ??
        list.guest ??
        list.guests ??
        [];
      return Array.isArray(g) ? g.length : 0;
    };
    const sorted = [...lists].sort((a, b) => {
      switch (listSortBy) {
        case "custom":
          // Keep manual order from guestLists (including drag-and-drop order).
          return 0;
        case "oldest":
          return getCreatedAt(a).localeCompare(getCreatedAt(b));
        case "newest":
          return getCreatedAt(b).localeCompare(getCreatedAt(a));
        case "most-guests":
          return getGuestCount(b) - getGuestCount(a);
        case "least-guests":
          return getGuestCount(a) - getGuestCount(b);
        case "last-edited":
          return getUpdatedAt(b).localeCompare(getUpdatedAt(a));
        case "first-edited":
          return getUpdatedAt(a).localeCompare(getUpdatedAt(b));
        default:
          return 0;
      }
    });
    return sorted;
  }, [guestLists, listSearchQuery, listSortBy]);

  const listsForReorder = useMemo(
    () => guestLists.filter((list) => !isUnassignedGuestList(list)),
    [guestLists]
  );

  const highlightListName = (name: string) => {
    const q = listSearchQuery.trim();
    if (!q) return name;
    const lower = name.toLowerCase();
    const lowerQ = q.toLowerCase();
    const i = lower.indexOf(lowerQ);
    if (i === -1) return name;
    return (
      <>
        {name.slice(0, i)}
        <mark className="bg-primary/30 text-foreground rounded px-0.5">
          {name.slice(i, i + q.length)}
        </mark>
        {name.slice(i + q.length)}
      </>
    );
  };

  useEffect(() => {
    if (!authLoading && !user) {
      router.push("/prijava");
    }
  }, [user, authLoading, router]);

  useEffect(() => {
    if (user && token && plannerListReady && activeEventNumericId != null) {
      setLoading(true);
      setGuestLists([]);
      setError(null);
      void loadGuestLists();
    } else if (
      user &&
      token &&
      plannerListReady &&
      activeEventNumericId == null
    ) {
      setGuestLists([]);
      setLoading(false);
    }
  }, [user, token, plannerListReady, activeEventNumericId]);

  // Helper function to normalize guest data (convert confirmed to status if needed)
  const normalizeGuest = (guest: Guest): Guest => {
    if (guest.status === "Bez stola") {
      return { ...guest, confirmed: false };
    }
    if (!guest.status && guest.confirmed !== undefined) {
      return {
        ...guest,
        status: guest.confirmed ? "Potvrđen" : "Na čekanju",
      };
    }
    if (!guest.status) {
      return {
        ...guest,
        status: "Na čekanju",
      };
    }
    return guest;
  };

  // Helper function to get status colors
  const getStatusColors = (status?: string) => {
    const guestStatus = status || "Na čekanju";

    if (guestStatus === "Potvrđen") {
      return {
        bg: "bg-[#31d158]/80",
        border: "border-[#31d158]/80",
        text: "text-black",
        badge: "bg-[#31d158]/80 text-black",
      };
    } else if (guestStatus === "Na čekanju") {
      return {
        bg: "bg-[#ffcc02]/80",
        border: "border-[#ffcc02]/80",
        text: "text-black",
        badge: "bg-[#ffcc02]/80 text-black",
      };
    } else if (guestStatus === "Otkazan") {
      return {
        bg: "bg-[#ff6165]/80",
        border: "border-[#ff6165]/80",
        text: "text-black",
        badge: "bg-[#ff6165]/80 text-black",
      };
    } else if (guestStatus === "Bez stola") {
      return {
        bg: "bg-sky-500/85",
        border: "border-sky-500/85",
        text: "text-black",
        badge: "bg-sky-500/85 text-black",
      };
    }
    // Default fallback
    return {
      bg: "bg-muted",
      border: "border-border",
      text: "text-muted-foreground",
      badge: "bg-muted text-muted-foreground",
    };
  };

  const loadGuestLists = async () => {
    if (!token || !user) return;
    const forEvent = activeEventNumericIdRef.current;
    if (forEvent == null) return;

    try {
      setLoading(true);
      setError(null);
      const lists = await getGuestLists(
        token,
        user.id,
        forEvent
      );
      if (activeEventNumericIdRef.current !== forEvent) return;
      // Normalize guest data to ensure status is set
      const normalizedLists = lists.map((list) => {
        const guests =
          list.attributes?.guest ||
          list.attributes?.guests ||
          list.guest ||
          list.guests ||
          [];
        const normalizedGuests = guests.map(normalizeGuest);
        return {
          ...list,
          guest: normalizedGuests,
          guests: normalizedGuests,
          attributes: list.attributes
            ? {
                ...list.attributes,
                guest: normalizedGuests,
                guests: normalizedGuests,
              }
            : undefined,
        };
      });

      // Apply stored display order (key by event id for this response, not current UI event)
      const storageKey = user
        ? `spisak-gostiju-order-${user.id}-${forEvent}`
        : null;
      if (storageKey) {
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const ids: number[] = JSON.parse(stored);
            const byId = new Map(normalizedLists.map((l) => [l.id, l]));
            const ordered: GuestList[] = [];
            for (const id of ids) {
              const list = byId.get(id);
              if (list) {
                ordered.push(list);
                byId.delete(id);
              }
            }
            byId.forEach((list) => ordered.push(list));
            if (activeEventNumericIdRef.current !== forEvent) return;
            setGuestLists(pinUnassignedGuestListLast(ordered));
          } else {
            if (activeEventNumericIdRef.current !== forEvent) return;
            setGuestLists(pinUnassignedGuestListLast(normalizedLists));
            localStorage.setItem(
              storageKey,
              JSON.stringify(normalizedLists.map((l) => l.id))
            );
          }
        } catch {
          if (activeEventNumericIdRef.current !== forEvent) return;
          setGuestLists(pinUnassignedGuestListLast(normalizedLists));
        }
      } else {
        if (activeEventNumericIdRef.current !== forEvent) return;
        setGuestLists(pinUnassignedGuestListLast(normalizedLists));
      }
    } catch (err) {
      console.error("Error loading guest lists:", err);
      if (activeEventNumericIdRef.current === forEvent) {
        setError(
          err instanceof Error
            ? err.message
            : "Greška pri učitavanju spiskova gostiju"
        );
      }
    } finally {
      if (activeEventNumericIdRef.current === forEvent) {
        setLoading(false);
      }
    }
  };

  const ensureUnassignedGuestList = async (): Promise<GuestList | null> => {
    if (!token || !user || activeEventNumericId == null) return null;
    const existing = guestLists.find((l) => isUnassignedGuestList(l));
    if (existing) return existing;
    const created = (await createGuestList(
      token,
      UNASSIGNED_GUEST_LIST_NAME,
      "Gosti koji još nisu dodijeljeni konkretnom spisku/stolu.",
      [],
      user.id,
      activeEventNumericId
    )) as GuestList;
    await loadGuestLists();
    return created;
  };

  const handleCreateList = async () => {
    if (!token || !user || !listName.trim()) {
      setListNameError("Naziv spiska je obavezan");
      return;
    }
    if (listName.trim() === UNASSIGNED_GUEST_LIST_NAME) {
      setListNameError(
        "Ovaj naziv je rezervisan za goste bez dodijeljenog spiska."
      );
      return;
    }
    if (activeEventNumericId == null) {
      setError("Izaberite ili kreirajte događaj na strani Profil.");
      return;
    }

    try {
      setError(null);
      setListNameError(null);
      const newList = await createGuestList(
        token,
        listName,
        listDescription,
        guests,
        user.id,
        activeEventNumericId
      );
      console.log("Created list with guests:", newList);
      // Reload all lists to ensure we have the latest data with guests populated
      await loadGuestLists();
      setIsCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error("Error creating guest list:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri kreiranju spiska gostiju"
      );
    }
  };

  const handleUpdateList = async () => {
    if (!token || !editingList || !listName.trim()) {
      setListNameError("Naziv spiska je obavezan");
      return;
    }

    try {
      setError(null);
      setListNameError(null);
      console.log("Updating list with guests:", guests);
      const updatedList = await updateGuestList(
        token,
        editingList.id,
        listName,
        listDescription,
        guests,
        editingList.documentId,
        activeEventNumericId ?? undefined
      );
      console.log("Updated list response:", updatedList);
      // Reload all lists to ensure we have the latest data with guests populated
      await loadGuestLists();
      setIsEditDialogOpen(false);
      setEditingList(null);
      resetForm();
    } catch (err) {
      console.error("Error updating guest list:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri ažuriranju spiska gostiju"
      );
    }
  };

  const saveListOrder = (lists: GuestList[]) => {
    if (user) {
      try {
        localStorage.setItem(
          `spisak-gostiju-order-${user.id}-${activeEventId ?? "none"}`,
          JSON.stringify(lists.map((l) => l.id))
        );
      } catch {}
    }
  };

  const handleReorderLists = (newOrder: GuestList[]) => {
    const tail = guestLists.filter((l) => isUnassignedGuestList(l));
    const merged = pinUnassignedGuestListLast([...newOrder, ...tail]);
    setGuestLists(merged);
    saveListOrder(merged);
  };

  const handleDeleteList = async (id: number) => {
    if (!token) return;

    try {
      setError(null);
      const listToDelete = guestLists.find((list) => list.id === id);
      if (listToDelete && isUnassignedGuestList(listToDelete)) {
        toast.error(
          "Spisak „Bez stola” ne može se obrisati. To je mjesto za goste koji još nemaju sto."
        );
        setDeleteListId(null);
        return;
      }
      await deleteGuestList(token, id, listToDelete?.documentId);
      const updated = guestLists.filter((list) => list.id !== id);
      setGuestLists(updated);
      saveListOrder(updated);
      setDeleteListId(null);
    } catch (err) {
      console.error("Error deleting guest list:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri brisanju spiska gostiju"
      );
    }
  };

  const handleEditClick = (list: GuestList) => {
    setEditingList(list);
    setListName(list.attributes?.name || list.name);
    setListNameError(null);
    // Support both "Description" (capital D) and "description" (lowercase)
    setListDescription(
      list.attributes?.Description ||
        list.attributes?.description ||
        list.Description ||
        list.description ||
        ""
    );
    // Support both "guests" (plural) and "guest" (singular component name)
    const listGuests =
      list.attributes?.guest ||
      list.attributes?.guests ||
      list.guest ||
      list.guests ||
      [];
    setGuests(listGuests.map(normalizeGuest));
    // Reset the current guest form
    setCurrentGuest({ name: "", phone: "", notes: "", status: "Na čekanju" });
    setEditingGuestIndex(null);
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setListName("");
    setListNameError(null);
    setListDescription("");
    setGuests([]);
    setCurrentGuest({ name: "", phone: "", notes: "", status: "Na čekanju" });
    setCurrentGuestFirstName("");
    setCurrentGuestLastName("");
    setEditingGuestIndex(null);
    setEditingGuest(null);
    setIsEditGuestDialogOpen(false);
    setEditingList(null);
    setEditingGuestTargetListId(null);
  };

  const addGuestToList = () => {
    const first = currentGuestFirstName.trim();
    const last = currentGuestLastName.trim();

    // Require at least ime (first name)
    if (!first) {
      return;
    }
    const fullName = `${first} ${last}`.trim();

    if (editingGuestIndex !== null) {
      // Update existing guest
      const updatedGuests = [...guests];
      updatedGuests[editingGuestIndex] = {
        ...currentGuest,
        name: fullName,
      };
      setGuests(updatedGuests);
      setEditingGuestIndex(null);
      if (isCreateDialogOpen) {
        toast.success("Podaci o gostu su ažurirani u spisku.", {
          duration: 5000,
        });
      }
    } else {
      // Add new guest
      setGuests([
        ...guests,
        {
          ...currentGuest,
          name: fullName,
        },
      ]);
      if (isCreateDialogOpen) {
        toast.success(`${fullName} je dodat u spisak.`, { duration: 5000 });
      }
    }

    // Clear the form for next guest
    setCurrentGuest({ name: "", phone: "", notes: "", status: "Na čekanju" });
    setCurrentGuestFirstName("");
    setCurrentGuestLastName("");
  };

  const startEditingGuest = (index: number, guestListId?: number) => {
    // If guestListId is provided, we're editing from the main page view
    if (guestListId !== undefined) {
      const guestList = guestLists.find((list) => list.id === guestListId);
      if (guestList) {
        const listGuests =
          guestList.attributes?.guest ||
          guestList.attributes?.guests ||
          guestList.guest ||
          guestList.guests ||
          [];
        if (listGuests[index]) {
          const normalized = normalizeGuest({ ...listGuests[index] });
          setEditingGuest(normalized);
          const parts = (normalized.name || "").trim().split(/\s+/);
          setEditingGuestFirstName(parts[0] || "");
          setEditingGuestLastName(
            parts.length > 1 ? parts.slice(1).join(" ") : ""
          );
          setEditingGuestIndex(index);
          setIsEditGuestDialogOpen(true);
          // Store the list ID so we can update it after editing
          setEditingList(guestList);
          setEditingGuestTargetListId(guestList.id);
        }
      }
    } else {
      // Editing from within a dialog (create/edit list)
      setEditingGuestIndex(index);
      const normalized = normalizeGuest({ ...guests[index] });
      setEditingGuest(normalized);
      const parts = (normalized.name || "").trim().split(/\s+/);
      setEditingGuestFirstName(parts[0] || "");
      setEditingGuestLastName(parts.length > 1 ? parts.slice(1).join(" ") : "");
      setEditingGuestTargetListId(editingList?.id ?? null);
      setIsEditGuestDialogOpen(true);
    }
  };

  const saveEditedGuest = async () => {
    if (editingGuestIndex !== null && editingGuest && token) {
      const fullName =
        `${editingGuestFirstName} ${editingGuestLastName}`.trim();
      let guestToSave: Guest = {
        ...editingGuest,
        name: fullName || editingGuest.name || "",
      };
      // If we're editing from the main page (editingList is set), update the list in Strapi
      if (editingList) {
        const sourceList = editingList;
        const targetListId = editingGuestTargetListId ?? sourceList.id;
        const targetList =
          guestLists.find((l) => l.id === targetListId) ?? sourceList;

        if (targetList.id !== sourceList.id && guestToSave.status === "Bez stola") {
          guestToSave = {
            ...guestToSave,
            status: "Na čekanju",
            confirmed: false,
          };
        }

        const sourceListGuests =
          sourceList.attributes?.guest ||
          sourceList.attributes?.guests ||
          sourceList.guest ||
          sourceList.guests ||
          [];

        // Move guest to another list when target differs.
        if (targetList.id !== sourceList.id) {
          const sourceUpdatedGuests = sourceListGuests.filter(
            (_: Guest, index: number) => index !== editingGuestIndex
          );
          const targetListGuests =
            targetList.attributes?.guest ||
            targetList.attributes?.guests ||
            targetList.guest ||
            targetList.guests ||
            [];
          const targetUpdatedGuests = [...targetListGuests, guestToSave];

          try {
            setError(null);

            await updateGuestList(
              token,
              sourceList.id,
              sourceList.attributes?.name || sourceList.name,
              sourceList.attributes?.Description ||
                sourceList.attributes?.description ||
                sourceList.Description ||
                sourceList.description,
              sourceUpdatedGuests,
              sourceList.documentId,
              activeEventNumericId ?? undefined
            );

            await updateGuestList(
              token,
              targetList.id,
              targetList.attributes?.name || targetList.name,
              targetList.attributes?.Description ||
                targetList.attributes?.description ||
                targetList.Description ||
                targetList.description,
              targetUpdatedGuests,
              targetList.documentId,
              activeEventNumericId ?? undefined
            );

            const currentOpenItems = openAccordionItems;
            const currentAllGuestsOpen = isAllGuestsAccordionOpen;
            await loadGuestLists();
            setOpenAccordionItems(currentOpenItems);
            setIsAllGuestsAccordionOpen(currentAllGuestsOpen);
            setIsEditGuestDialogOpen(false);
            setEditingGuestIndex(null);
            setEditingGuest(null);
            setEditingList(null);
            setEditingGuestTargetListId(null);
          } catch (err) {
            console.error("Error moving guest to another list:", err);
            setError(
              err instanceof Error
                ? err.message
                : "Greška pri premještanju gosta"
            );
          }
          return;
        }

        const listGuests =
          editingList.attributes?.guest ||
          editingList.attributes?.guests ||
          editingList.guest ||
          editingList.guests ||
          [];
        const updatedGuests = [...listGuests];
        updatedGuests[editingGuestIndex] = guestToSave;

        try {
          setError(null);
          await updateGuestList(
            token,
            editingList.id,
            editingList.attributes?.name || editingList.name,
            editingList.attributes?.Description ||
              editingList.attributes?.description ||
              editingList.Description ||
              editingList.description,
            updatedGuests,
            editingList.documentId,
            activeEventNumericId ?? undefined
          );
          // Reload lists to get updated data
          // Preserve accordion state before reloading
          const currentOpenItems = openAccordionItems;
          const currentAllGuestsOpen = isAllGuestsAccordionOpen;
          await loadGuestLists();
          // Restore accordion state after reloading
          setOpenAccordionItems(currentOpenItems);
          setIsAllGuestsAccordionOpen(currentAllGuestsOpen);
          setIsEditGuestDialogOpen(false);
          setEditingGuestIndex(null);
          setEditingGuest(null);
          setEditingList(null);
          setEditingGuestTargetListId(null);
        } catch (err) {
          console.error("Error updating guest:", err);
          setError(
            err instanceof Error ? err.message : "Greška pri ažuriranju gosta"
          );
        }
      } else {
        // Editing from within a dialog (create/edit list)
        const updatedGuests = [...guests];
        updatedGuests[editingGuestIndex] = guestToSave;
        setGuests(updatedGuests);
        setIsEditGuestDialogOpen(false);
        setEditingGuestIndex(null);
        setEditingGuest(null);
        setEditingGuestTargetListId(null);
      }
    }
  };

  const cancelEditingGuest = () => {
    setEditingGuestIndex(null);
    setEditingGuest(null);
    setEditingGuestFirstName("");
    setEditingGuestLastName("");
    setEditingGuestTargetListId(null);
    setIsEditGuestDialogOpen(false);
  };

  const handleDeleteGuest = async (listId: number, guestIndex: number) => {
    if (!token) return;

    try {
      setError(null);
      const list = guestLists.find((l) => l.id === listId);
      if (!list) return;

      const listGuests =
        list.attributes?.guest ||
        list.attributes?.guests ||
        list.guest ||
        list.guests ||
        [];
      const updatedGuests = listGuests.filter(
        (_: Guest, index: number) => index !== guestIndex
      );

      await updateGuestList(
        token,
        listId,
        list.attributes?.name || list.name,
        list.attributes?.Description ||
          list.attributes?.description ||
          list.Description ||
          list.description,
        updatedGuests,
        list.documentId,
        activeEventNumericId ?? undefined
      );

      // Reload lists to get updated data
      await loadGuestLists();
      setDeletingGuest(null);
    } catch (err) {
      console.error("Error deleting guest:", err);
      setError(
        err instanceof Error ? err.message : "Greška pri brisanju gosta"
      );
    }
  };

  const getLastName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 0) return "";
    return parts[parts.length - 1];
  };

  // Get all guests from all lists with their list information
  const getAllGuestsWithListInfo = () => {
    const allGuests: Array<{
      guest: Guest;
      listId: number;
      listName: string;
      guestIndex: number;
    }> = [];

    guestLists.forEach((list) => {
      const rawListName = list.attributes?.name || list.name;
      const listName = isUnassignedGuestList(list) ? "Bez stola" : rawListName;
      const listGuests =
        list.attributes?.guest ||
        list.attributes?.guests ||
        list.guest ||
        list.guests ||
        [];

      listGuests.forEach((guest: Guest, index: number) => {
        allGuests.push({
          guest,
          listId: list.id,
          listName,
          guestIndex: index,
        });
      });
    });

    return allGuests;
  };

  // Filter guests based on search query and status
  const getFilteredGuests = () => {
    const allGuests = getAllGuestsWithListInfo();

    let filtered = allGuests;

    // Filter by search query
    if (guestSearchQuery.trim()) {
      const query = normalizeForSearch(guestSearchQuery);
      filtered = filtered.filter(({ guest }) =>
        searchIncludes(guest.name ?? "", query)
      );
    }

    // Filter by status
    if (guestStatusFilter !== "Svi") {
      filtered = filtered.filter(({ guest, listId }) => {
        const list = guestLists.find((l) => l.id === listId);
        const guestStatus =
          guest.status || (guest.confirmed ? "Potvrđen" : "Na čekanju");
        if (guestStatusFilter === "Bez stola") {
          return (
            guestStatus === "Bez stola" ||
            (list != null && isUnassignedGuestList(list))
          );
        }
        return guestStatus === guestStatusFilter;
      });
    }

    const sorted = [...filtered].sort(({ guest: a }, { guest: b }) => {
      const fullA = (a.name || "").toString();
      const fullB = (b.name || "").toString();
      const lastA = getLastName(fullA).toLowerCase();
      const lastB = getLastName(fullB).toLowerCase();

      const lastCmp = lastA.localeCompare(lastB, "sr", { sensitivity: "base" });
      if (lastCmp !== 0) return lastCmp;

      return fullA.localeCompare(fullB, "sr", { sensitivity: "base" });
    });

    return sorted;
  };

  const updateGuestStatus = (
    index: number,
    status: "Potvrđen" | "Na čekanju" | "Otkazan"
  ) => {
    const updatedGuests = [...guests];
    updatedGuests[index] = {
      ...updatedGuests[index],
      status: status,
      confirmed: status === "Potvrđen", // Keep for backward compatibility
    };
    setGuests(updatedGuests);
  };

  const exportToPDF = async () => {
    if (hasNoEvents) return;
    if (guestLists.length === 0) {
      setError("Nema spiskova za izvoz");
      return;
    }

    try {
      const [{ jsPDF, autoTable }] = await Promise.all([loadPDF(), loadPDFLogo()]);

      const doc = new jsPDF();
      const date = new Date().toLocaleDateString("sr-RS");
      let pageNum = 1;

      // ── Compute totals ────────────────────────────────────────────────────
      let totalAll = 0, totalConfirmed = 0, totalPending = 0, totalCancelled = 0;
      guestLists.forEach((list) => {
        const guests: Guest[] = list.attributes?.guest ?? list.attributes?.guests ?? list.guest ?? list.guests ?? [];
        totalAll += guests.length;
        guests.forEach((g) => {
          const st = g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
          if (st === "Potvrđen") totalConfirmed++;
          else if (st === "Na čekanju") totalPending++;
          else if (st === "Otkazan") totalCancelled++;
        });
      });

      const guestListPdfTitle = "Spisak gostiju i stolova";

      // ── First page header ─────────────────────────────────────────────────
      drawPDFHeader(doc, guestListPdfTitle, `Datum: ${date}`);

      let yPos = drawStatRow(doc, [
        { label: "Ukupno",     value: String(totalAll) },
        { label: "Potvrđeno",  value: String(totalConfirmed) },
        { label: "Na čekanju", value: String(totalPending) },
        { label: "Otkazanih",  value: String(totalCancelled) },
      ], 48);

      yPos += 4;

      const base = getPDFTableStyles();

      // ── Per-list tables ───────────────────────────────────────────────────
      for (const list of guestLists) {
        const listName: string = list.attributes?.name ?? list.name ?? "Bez naziva";
        const listDescription: string =
          list.attributes?.Description ?? list.attributes?.description ??
          list.Description ?? list.description ?? "";
        const listGuests: Guest[] =
          list.attributes?.guest ?? list.attributes?.guests ??
          list.guest ?? list.guests ?? [];

        // Manual page break before section label if near bottom
        if (yPos > maxContentY(doc) - 30) {
          drawPDFFooter(doc, pageNum);
          pageNum++;
          doc.addPage();
          drawPDFHeader(doc, guestListPdfTitle, `Datum: ${date}`);
          yPos = 48;
        }

        yPos = drawSectionLabel(doc, listName, yPos);

        if (listDescription) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8.5);
          doc.setTextColor(...C.gray);
          const descLines = doc.splitTextToSize(fixSerbianText(listDescription), 178) as string[];
          doc.text(descLines, MARGIN, yPos);
          yPos += descLines.length * 4 + 3;
        }

        const tableData = listGuests.map((g) => {
          const st = g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
          return [
            "",
            fixSerbianText(g.name || ""),
            g.phone || "-",
            fixSerbianText(st),
          ];
        });

        autoTable(doc, {
          ...base,
          startY: yPos,
          head: [["", "Ime i prezime", "Telefon", "Status"]],
          body: tableData,
          columnStyles: {
            0: { cellWidth: 10, halign: "center", valign: "middle" },
            1: { cellWidth: 88, halign: "left", valign: "middle" },
            2: { cellWidth: 52, halign: "left", valign: "middle" },
            3: { cellWidth: 30, halign: "left", valign: "middle" },
          },
          didParseCell: (data: any) => {
            if (data.section === "body" && data.column.index === 3) {
              const val = data.cell.raw as string;
              if (val === "Potvrden" || val === "Potvrđen") {
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.textColor = C.dark;
              } else if (val === "Otkazan") {
                data.cell.styles.fontStyle = "italic";
                data.cell.styles.textColor = C.gray;
              } else {
                data.cell.styles.textColor = C.dark;
              }
            }
          },
          didDrawCell: (data: any) => {
            if (data.section === "body" && data.column.index === 0) {
              drawPDFTableCheckbox(doc, data.cell);
            }
          },
          didDrawPage: (data: any) => {
            if (data.pageNumber > 1) {
              drawPDFHeader(doc, guestListPdfTitle, `Datum: ${date}`);
            }
            drawPDFFooter(doc, pageNum + data.pageNumber - 1);
          },
        } as any);

        pageNum += ((doc as any).lastAutoTable?.pageCount ?? 1) - 1;
        yPos = (doc as any).lastAutoTable.finalY + 12;
      }

      drawPDFFooter(doc, pageNum);
      doc.save(`Spisak-gostiju-i-stolova-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setError("Greška pri konvertovanju u PDF");
    }
  };

  const exportAllGuestsChecklistToPDF = async () => {
    if (hasNoEvents) return;
    if (guestLists.length === 0) {
      setError("Nema gostiju za izvoz");
      return;
    }

    try {
      const allGuestsWithInfo = getAllGuestsWithListInfo();
      if (allGuestsWithInfo.length === 0) {
        setError("Nema gostiju za izvoz");
        return;
      }

      const sortedGuests = [...allGuestsWithInfo].sort((a, b) => {
        const fullA = (a.guest.name || "").toString();
        const fullB = (b.guest.name || "").toString();
        const lastA = getLastName(fullA).toLowerCase();
        const lastB = getLastName(fullB).toLowerCase();
        const cmp = lastA.localeCompare(lastB, "sr", { sensitivity: "base" });
        return cmp !== 0 ? cmp : fullA.localeCompare(fullB, "sr", { sensitivity: "base" });
      });

      // ── Overview totals ───────────────────────────────────────────────────
      let totalConfirmed = 0;
      let totalPending = 0;
      let totalCancelled = 0;
      for (const { guest } of sortedGuests) {
        const st = guest.status || (guest.confirmed ? "Potvrđen" : "Na čekanju");
        if (st === "Potvrđen") totalConfirmed++;
        else if (st === "Otkazan") totalCancelled++;
        else totalPending++;
      }

      const [{ jsPDF, autoTable }] = await Promise.all([loadPDF(), loadPDFLogo()]);

      const doc = new jsPDF();
      const date = new Date().toLocaleDateString("sr-RS");
      let pageNum = 1;

      drawPDFHeader(doc, "Svi gosti", `Datum: ${date}`);

      let yPos = drawStatRow(doc, [
        { label: "Ukupno",     value: String(sortedGuests.length) },
        { label: "Potvrđeno",  value: String(totalConfirmed) },
        { label: "Na čekanju", value: String(totalPending) },
        { label: "Otkazanih",  value: String(totalCancelled) },
      ], 48);

      yPos += 2;

      const tableBody = sortedGuests.map(({ guest, listName }) => {
        const fullName = (guest.name || "").toString();
        const parts = fullName.trim().split(/\s+/);
        const firstName = parts[0] ?? "";
        const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
        const tableNumber =
          (guest as any).tableNumber ?? (guest as any).sto ?? (guest as any).table ?? listName;
        const status =
          guest.status || (guest.confirmed ? "Potvrđen" : "Na čekanju");

        return [
          "",
          fixSerbianText(lastName ? `${lastName} ${firstName}`.trim() : firstName),
          fixSerbianText(String(tableNumber ?? "")),
          fixSerbianText(status),
        ];
      });

      const base = getPDFTableStyles();
      autoTable(doc, {
        ...base,
        startY: yPos,
        head: [["", "Ime i prezime", "Sto / Spisak", "Status"]],
        body: tableBody,
        columnStyles: {
          0: { cellWidth: 10, halign: "center", valign: "middle" },
          1: { cellWidth: 92, halign: "left", valign: "middle" },
          2: { cellWidth: 48, halign: "left", valign: "middle" },
          3: { cellWidth: 30, halign: "left", valign: "middle" },
        },
        didParseCell: (data: any) => {
          if (data.section === "body" && data.column.index === 3) {
            const val = data.cell.raw as string;
            if (val === "Potvrden" || val === "Potvrđen") {
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.textColor = C.dark;
            } else if (val === "Otkazan") {
              data.cell.styles.fontStyle = "italic";
              data.cell.styles.textColor = C.gray;
            } else {
              data.cell.styles.textColor = C.dark;
            }
          }
        },
        didDrawCell: (data: any) => {
          if (data.section === "body" && data.column.index === 0) {
            drawPDFTableCheckbox(doc, data.cell);
          }
        },
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            drawPDFHeader(doc, "Svi gosti", `Datum: ${date}`);
          }
          drawPDFFooter(doc, pageNum + data.pageNumber - 1);
        },
      } as any);

      doc.save(`Svi-gosti-checklista-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting all guests checklist PDF:", error);
      setError("Greška pri konvertovanju u PDF");
    }
  };

  const removeGuest = (index: number) => {
    setGuests(guests.filter((_, i) => i !== index));
  };

  const updateCurrentGuest = (field: keyof Guest, value: string | boolean) => {
    if (field === "status") {
      const st = value as Guest["status"];
      setCurrentGuest({
        ...currentGuest,
        status: st,
        confirmed: st === "Potvrđen",
      });
    } else {
      setCurrentGuest({ ...currentGuest, [field]: value });
    }
  };

  const handleGuestFormKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      addGuestToList();
    }
  };

  const resetNewGuestForm = () => {
    setNewGuestListId("");
    setNewGuestFirstName("");
    setNewGuestLastName("");
    setNewGuest({ name: "", phone: "", notes: "", status: "Na čekanju" });
  };

  const updateNewGuest = (field: keyof Guest, value: string | boolean) => {
    if (field === "status" && typeof value === "string") {
      const st = value as Guest["status"];
      setNewGuest({
        ...newGuest,
        status: st,
        confirmed: st === "Potvrđen",
      });
    } else if (field === "confirmed" && typeof value === "boolean") {
      setNewGuest({
        ...newGuest,
        confirmed: value,
        status: value ? "Potvrđen" : "Na čekanju",
      });
    } else {
      setNewGuest({ ...newGuest, [field]: value });
    }
  };

  const handleNewGuestFormKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      void handleCreateGuestToSelectedList();
    }
  };

  const handleCreateGuestToSelectedList = async () => {
    if (!token || !newGuestFirstName.trim()) return;

    let selectedList: GuestList;

    if (!newGuestListId) {
      const unassigned = await ensureUnassignedGuestList();
      if (!unassigned) return;
      selectedList = unassigned;
    } else {
      const selectedListId = Number(newGuestListId);
      const found = guestLists.find((list) => list.id === selectedListId);
      if (!found) return;
      selectedList = found;
    }

    const fullName =
      `${newGuestFirstName.trim()} ${newGuestLastName.trim()}`.trim();
    const arrivalStatus =
      (newGuest.status as Guest["status"]) === "Bez stola" ||
      !newGuest.status
        ? "Na čekanju"
        : (newGuest.status as Guest["status"]);
    const guestToAdd: Guest = {
      ...newGuest,
      name: fullName,
      status: arrivalStatus,
      confirmed: arrivalStatus === "Potvrđen",
    };

    const listGuests =
      selectedList.attributes?.guest ||
      selectedList.attributes?.guests ||
      selectedList.guest ||
      selectedList.guests ||
      [];
    const updatedGuests = [...listGuests, guestToAdd];

    try {
      setError(null);
      await updateGuestList(
        token,
        selectedList.id,
        selectedList.attributes?.name || selectedList.name,
        selectedList.attributes?.Description ||
          selectedList.attributes?.description ||
          selectedList.Description ||
          selectedList.description,
        updatedGuests,
        selectedList.documentId,
        activeEventNumericId ?? undefined
      );

      const currentOpenItems = openAccordionItems;
      const currentAllGuestsOpen = isAllGuestsAccordionOpen;
      await loadGuestLists();
      setOpenAccordionItems(currentOpenItems);
      setIsAllGuestsAccordionOpen(currentAllGuestsOpen);

      setIsCreateGuestDialogOpen(false);
      resetNewGuestForm();
    } catch (err) {
      console.error("Error creating guest in selected list:", err);
      setError(
        err instanceof Error ? err.message : "Greška pri dodavanju gosta"
      );
    }
  };

  console.log("");

  if (authLoading || loading) {
    return (
      <EventDataLoading className="min-h-screen flex flex-col items-center justify-center gap-8 px-3 py-4 md:py-6 bg-gradient-to-br from-background to-muted" />
    );
  }

  if (!user) {
    return null;
  }

  const filteredGuests = getFilteredGuests();

  return (
    <div className="min-h-screen px-3 py-4 md:py-6 pb-20 lg:pb-6">
      <div className="max-w-[1600px] mx-auto space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Gosti</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Upravljajte spiskovima gostiju za vašu svadbu
            </p>
            {hasNoEvents && (
              <PlannerEventRequiredNotice
                message="Da bi koristili spisak gostiju, morate prvo kreirati proslavu na profilu."
                className="w-fit max-w-full"
              />
            )}
            {guestLists.length > 0 &&
              (() => {
                const allGuests = guestLists.flatMap((list) => {
                  const guests =
                    list.attributes?.guest ||
                    list.attributes?.guests ||
                    list.guest ||
                    list.guests ||
                    [];
                  return guests;
                });
                const totalGuests = allGuests.length;
                const confirmedGuests = allGuests.filter((g: Guest) => {
                  const status =
                    g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
                  return status === "Potvrđen";
                }).length;
                const pendingGuests = allGuests.filter((g: Guest) => {
                  const status =
                    g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
                  return status === "Na čekanju";
                }).length;
                const cancelledGuests = allGuests.filter((g: Guest) => {
                  const status =
                    g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
                  return status === "Otkazan";
                }).length;
                const bezStolaGuests = allGuests.filter((g: Guest) => {
                  const status =
                    g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
                  return status === "Bez stola";
                }).length;

                return (
                  <div className="mt-3 grid grid-cols-2 sm:flex flex-wrap gap-2 sm:gap-4 text-sm">
                    <span className="text-muted-foreground">
                      Ukupno:{" "}
                      <span className="font-semibold text-foreground">
                        {totalGuests}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      Potvrđenih:{" "}
                      <span className="font-semibold text-foreground">
                        {confirmedGuests}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      Na čekanju:{" "}
                      <span className="font-semibold text-foreground">
                        {pendingGuests}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      Otkazanih:{" "}
                      <span className="font-semibold text-foreground">
                        {cancelledGuests}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      Bez stola:{" "}
                      <span className="font-semibold text-foreground">
                        {bezStolaGuests}
                      </span>
                    </span>
                  </div>
                );
              })()}
          </div>
        </div>
        <div
          className={
            hasNoEvents
              ? "pointer-events-none select-none opacity-[0.65]"
              : undefined
          }
        >
          <div className="flex w-full min-w-0 flex-col sm:flex-row sm:flex-wrap sm:justify-end items-stretch sm:items-center gap-2 mb-5 sm:mb-6">
            <div className="flex w-full min-w-0 flex-col gap-2 sm:contents">
              {guestLists.length > 0 && (
                <Button
                  variant="outline"
                  onClick={exportToPDF}
                  className="w-full min-w-0 sm:w-auto flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4 shrink-0" />
                  Raspored gostiju PDF
                </Button>
              )}
              {guestLists.length > 0 && (
                <Button
                  variant="outline"
                  onClick={exportAllGuestsChecklistToPDF}
                  className="w-full min-w-0 sm:w-auto flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4 shrink-0" />
                  Svi gosti PDF
                </Button>
              )}
              {listsForReorder.length > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setIsReorderDialogOpen(true)}
                  className="w-full min-w-0 sm:w-auto flex items-center justify-center gap-2"
                >
                  <ListOrdered className="h-4 w-4 shrink-0" />
                  Redosljed spiskova
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-3 w-full min-w-0 sm:contents">
              <Button
                onClick={() => {
                  resetForm();
                  setIsCreateDialogOpen(true);
                }}
                className="w-full min-w-0 sm:w-auto"
                disabled={hasNoEvents}
              >
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                Novi spisak
              </Button>
              <Button
                onClick={() => {
                  resetNewGuestForm();
                  setIsCreateGuestDialogOpen(true);
                }}
                className="w-full min-w-0 sm:w-auto"
                disabled={hasNoEvents}
              >
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                Novi gost
              </Button>
            </div>
          </div>
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
              {error}
            </div>
          )}
          {/* Mobile: Guests Accordion (below 1024px) */}
          <div className="lg:hidden w-full min-w-0">
            <Accordion
              type="single"
              collapsible
              className="w-full"
              value={isAllGuestsAccordionOpen}
              onValueChange={(value) => setIsAllGuestsAccordionOpen(value)}
            >
              <AccordionItem
                value="all-guests"
                className="border rounded-lg bg-card"
              >
                <AccordionHeader>
                  <AccordionTrigger className="hover:no-underline !px-4 !py-4 text-base font-semibold">
                    Svi gosti ({filteredGuests.length})
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="px-4 !pb-4">
                  <div className="space-y-4">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Pretraži goste po imenu..."
                        value={guestSearchQuery}
                        onChange={(e) => setGuestSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Status Filter */}
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">
                        Filtriraj po statusu
                      </Label>
                      <Select
                        value={guestStatusFilter}
                        onValueChange={setGuestStatusFilter}
                      >
                        <SelectTrigger className="text-base md:text-sm">
                          <SelectValue placeholder="Svi statusi" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Svi">Svi</SelectItem>
                          <SelectItem value="Potvrđen">Potvrđen</SelectItem>
                          <SelectItem value="Na čekanju">Na čekanju</SelectItem>
                          <SelectItem value="Otkazan">Otkazan</SelectItem>
                          <SelectItem value="Bez stola">
                            Bez stola (nije na spisku)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Guests List */}
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredGuests.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {guestSearchQuery || guestStatusFilter !== "Svi"
                            ? "Nema gostiju koji odgovaraju filterima"
                            : "Nema gostiju"}
                        </p>
                      ) : (
                        filteredGuests.map(
                          ({ guest, listId, listName, guestIndex }) => {
                            const fullName = (guest.name || "").toString();
                            const parts = fullName.trim().split(/\s+/);
                            const firstName =
                              parts.length === 1
                                ? parts[0] || ""
                                : parts[0] || "";
                            const lastName =
                              parts.length > 1 ? parts.slice(1).join(" ") : "";
                            const displayName = lastName
                              ? `${lastName} ${firstName}`.trim()
                              : firstName;

                            return (
                              <div
                                key={`${listId}-${guestIndex}`}
                                className="p-3 rounded-xl border bg-card text-card-foreground shadow-sm"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm sm:text-base">
                                      {displayName}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-xs text-muted-foreground">
                                        {listName}
                                      </span>
                                      <span
                                        className={`text-xs sm:text-sm px-2 py-0.5 rounded ${
                                          getStatusColors(
                                            guest.status ||
                                              (guest.confirmed
                                                ? "Potvrđen"
                                                : "Na čekanju")
                                          ).badge
                                        }`}
                                      >
                                        {guest.status ||
                                          (guest.confirmed
                                            ? "Potvrđen"
                                            : "Na čekanju")}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-10 w-10 p-0 hover:bg-black/5 dark:hover:bg-white/10 dark:hover:text-accent-foreground"
                                      onClick={() =>
                                        startEditingGuest(guestIndex, listId)
                                      }
                                    >
                                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                                          onClick={() =>
                                            setDeletingGuest({
                                              listId,
                                              guestIndex,
                                              guestName: guest.name || "",
                                            })
                                          }
                                        >
                                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>
                                            Potvrdite brisanje gosta
                                          </AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Da li ste sigurni da želite da
                                            obrišete gosta "{guest.name}" iz
                                            spiska "{listName}"? Ova akcija se
                                            ne može poništiti.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>
                                            Otkaži
                                          </AlertDialogCancel>
                                          <AlertDialogAction
                                            size="default"
                                            className="h-10"
                                            onClick={() =>
                                              handleDeleteGuest(
                                                listId,
                                                guestIndex
                                              )
                                            }
                                          >
                                            Obriši
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          {/* Main Content: Two-column layout on desktop */}
          <div className="lg:grid lg:grid-cols-[1fr_400px] lg:gap-4 mt-4 lg:mt-0">
            {/* Left: Guest Lists */}
            <div className="space-y-4 sm:space-y-6">
              {guestLists.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground mb-4">
                      Nemate kreiranih spiskova gostiju.
                    </p>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Kreiraj prvi spisak
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* List search and sort */}
                  <div className="flex w-full min-w-0 flex-col sm:flex-row gap-3">
                    <div className="relative w-full min-w-0 mt-3 lg:mt-0 sm:flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Pretraži spiskove po nazivu..."
                        value={listSearchQuery}
                        onChange={(e) => setListSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <Select value={listSortBy} onValueChange={setListSortBy}>
                      <SelectTrigger className="w-full sm:w-[220px] sm:mt-3 lg:mt-0">
                        <SelectValue placeholder="Sortiraj" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">
                          Redosljed spiskova
                        </SelectItem>
                        <SelectItem value="newest">Najnoviji</SelectItem>
                        <SelectItem value="oldest">Najstariji</SelectItem>
                        <SelectItem value="most-guests">
                          Najviše gostiju
                        </SelectItem>
                        <SelectItem value="least-guests">
                          Najmanje gostiju
                        </SelectItem>
                        <SelectItem value="last-edited">
                          Zadnji uređeni
                        </SelectItem>
                        <SelectItem value="first-edited">
                          Prvi uređeni
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Mobile/Tablet: Accordion Layout (<1024px) */}
                  <div className="lg:hidden">
                    {displayLists.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        {listSearchQuery.trim()
                          ? "Nema spiskova koji odgovaraju pretrazi."
                          : "Nema spiskova."}
                      </p>
                    ) : (
                      <Accordion
                        type="multiple"
                        className="w-full space-y-2"
                        value={openAccordionItems}
                        onValueChange={setOpenAccordionItems}
                      >
                        {displayLists.map((list) => {
                          const listName = (list.attributes?.name ??
                            list.name ??
                            "") as string;
                          const listDescription =
                            list.attributes?.Description ||
                            list.attributes?.description ||
                            list.Description ||
                            list.description;
                          const listGuestsRaw =
                            list.attributes?.guest ||
                            list.attributes?.guests ||
                            list.guest ||
                            list.guests ||
                            [];
                          const listGuests = listGuestsRaw
                            .map((guest: Guest, originalIndex: number) => ({
                              guest,
                              originalIndex,
                            }))
                            .sort((a, b) => {
                              const fullA = (a.guest.name || "").toString();
                              const fullB = (b.guest.name || "").toString();
                              const partsA = fullA.trim().split(/\s+/);
                              const partsB = fullB.trim().split(/\s+/);
                              const firstA =
                                partsA.length === 1
                                  ? partsA[0] || ""
                                  : partsA[0] || "";
                              const firstB =
                                partsB.length === 1
                                  ? partsB[0] || ""
                                  : partsB[0] || "";
                              const lastA =
                                partsA.length > 1
                                  ? partsA.slice(1).join(" ")
                                  : "";
                              const lastB =
                                partsB.length > 1
                                  ? partsB.slice(1).join(" ")
                                  : "";
                              const displayA = (
                                lastA ? `${lastA} ${firstA}` : firstA
                              ).toLowerCase();
                              const displayB = (
                                lastB ? `${lastB} ${firstB}` : firstB
                              ).toLowerCase();
                              return displayA.localeCompare(displayB, "sr", {
                                sensitivity: "base",
                              });
                            });

                          return (
                            <AccordionItem
                              key={list.id}
                              value={`list-${list.id}`}
                              className="border rounded-lg bg-card"
                            >
                              <AccordionHeader>
                                <AccordionTrigger className="hover:no-underline !px-4 !py-4">
                                  <div className="flex flex-col items-start gap-2 flex-1">
                                    <span className="font-semibold text-base">
                                      {highlightListName(listName)}
                                    </span>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 [&_span]:text-left text-xs sm:text-sm text-muted-foreground flex-wrap">
                                      <span>Gostiju: {listGuests.length}</span>
                                      <span>
                                        Potvrđenih:{" "}
                                        {
                                          listGuests.filter(({ guest: g }) => {
                                            const status =
                                              g.status ||
                                              (g.confirmed
                                                ? "Potvrđen"
                                                : "Na čekanju");
                                            return status === "Potvrđen";
                                          }).length
                                        }
                                      </span>
                                      <span>
                                        Na čekanju:{" "}
                                        {
                                          listGuests.filter(({ guest: g }) => {
                                            const status =
                                              g.status ||
                                              (g.confirmed
                                                ? "Potvrđen"
                                                : "Na čekanju");
                                            return status === "Na čekanju";
                                          }).length
                                        }
                                      </span>
                                      <span>
                                        Otkazanih:{" "}
                                        {
                                          listGuests.filter(({ guest: g }) => {
                                            const status =
                                              g.status ||
                                              (g.confirmed
                                                ? "Potvrđen"
                                                : "Na čekanju");
                                            return status === "Otkazan";
                                          }).length
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </AccordionTrigger>
                              </AccordionHeader>
                              <AccordionContent className="px-4 !pb-4">
                                <div className="space-y-4">
                                  {listDescription && (
                                    <p className="text-sm text-muted-foreground">
                                      {listDescription}
                                    </p>
                                  )}
                                  {listGuests.length > 0 && (
                                    <div className="space-y-1.5 overflow-y-auto">
                                      {listGuests.map(
                                        ({ guest, originalIndex }) => (
                                          <div
                                            key={`${guest.name}-${originalIndex}`}
                                            className="text-sm p-2 rounded flex items-start justify-between gap-2 bg-muted"
                                          >
                                            <div className="flex-1 min-w-0">
                                              <p className="font-medium text-sm sm:text-base truncate">
                                                {guest.name}
                                              </p>
                                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                {guest.phone && (
                                                  <span className="text-xs text-muted-foreground truncate">
                                                    {guest.phone}
                                                  </span>
                                                )}
                                                <span
                                                  className={`text-xs px-2 py-0.5 rounded ${
                                                    getStatusColors(
                                                      guest.status ||
                                                        (guest.confirmed
                                                          ? "Potvrđen"
                                                          : "Na čekanju")
                                                    ).badge
                                                  }`}
                                                >
                                                  {guest.status ||
                                                    (guest.confirmed
                                                      ? "Potvrđen"
                                                      : "Na čekanju")}
                                                </span>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 hover:bg-black/5 dark:hover:bg-white/10 dark:hover:text-accent-foreground"
                                                onClick={() =>
                                                  startEditingGuest(
                                                    originalIndex,
                                                    list.id
                                                  )
                                                }
                                              >
                                                <Edit className="h-3 w-3" />
                                              </Button>
                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                                                    onClick={() =>
                                                      setDeletingGuest({
                                                        listId: list.id,
                                                        guestIndex:
                                                          originalIndex,
                                                        guestName:
                                                          guest.name || "",
                                                      })
                                                    }
                                                  >
                                                    <Trash2 className="h-3 w-3" />
                                                  </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                  <AlertDialogHeader>
                                                    <AlertDialogTitle>
                                                      Da li ste sigurni?
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                      Ova akcija će obrisati
                                                      gosta "{guest.name}" iz
                                                      spiska. Ova akcija se ne
                                                      može poništiti.
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel>
                                                      Otkaži
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                      onClick={() =>
                                                        handleDeleteGuest(
                                                          list.id,
                                                          originalIndex
                                                        )
                                                      }
                                                      className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                    >
                                                      Obriši
                                                    </AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                  <div className="flex gap-2 pt-2 ml-auto w-fit items-center">
                                    <Button
                                      variant="default"
                                      className="flex-1 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                                      onClick={() => handleEditClick(list)}
                                    >
                                      <Edit className="mr-1 h-3 w-3" />
                                      <span className="inline">Izmjeni</span>
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          className="h-10 px-2 sm:px-3"
                                          onClick={() =>
                                            setDeleteListId(list.id)
                                          }
                                        >
                                          <Trash2 className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">
                                            Obriši
                                          </span>
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>
                                            Da li ste sigurni?
                                          </AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Ova akcija će obrisati spisak "
                                            {listName}" i sve goste u njemu. Ova
                                            akcija se ne može poništiti.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel
                                            onClick={() =>
                                              setDeleteListId(null)
                                            }
                                          >
                                            Otkaži
                                          </AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() =>
                                              handleDeleteList(list.id)
                                            }
                                            className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Obriši
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </div>
                  {/* Desktop: Card Grid Layout (>=1024px) */}
                  <div className="hidden lg:grid lg:grid-cols-2 gap-4">
                    {displayLists.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 col-span-2 text-center">
                        {listSearchQuery.trim()
                          ? "Nema spiskova koji odgovaraju pretrazi."
                          : "Nema spiskova."}
                      </p>
                    ) : (
                      <>
                        {displayLists.map((list) => {
                          const listName = (list.attributes?.name ??
                            list.name ??
                            "") as string;
                          const listDescription =
                            list.attributes?.Description ||
                            list.attributes?.description ||
                            list.Description ||
                            list.description;
                          const listGuestsRaw =
                            list.attributes?.guest ||
                            list.attributes?.guests ||
                            list.guest ||
                            list.guests ||
                            [];
                          const listGuests = listGuestsRaw
                            .map((guest: Guest, originalIndex: number) => ({
                              guest,
                              originalIndex,
                            }))
                            .sort((a, b) => {
                              const fullA = (a.guest.name || "").toString();
                              const fullB = (b.guest.name || "").toString();
                              const partsA = fullA.trim().split(/\s+/);
                              const partsB = fullB.trim().split(/\s+/);
                              const firstA =
                                partsA.length === 1
                                  ? partsA[0] || ""
                                  : partsA[0] || "";
                              const firstB =
                                partsB.length === 1
                                  ? partsB[0] || ""
                                  : partsB[0] || "";
                              const lastA =
                                partsA.length > 1
                                  ? partsA.slice(1).join(" ")
                                  : "";
                              const lastB =
                                partsB.length > 1
                                  ? partsB.slice(1).join(" ")
                                  : "";
                              const displayA = (
                                lastA ? `${lastA} ${firstA}` : firstA
                              ).toLowerCase();
                              const displayB = (
                                lastB ? `${lastB} ${firstB}` : firstB
                              ).toLowerCase();
                              return displayA.localeCompare(displayB, "sr", {
                                sensitivity: "base",
                              });
                            });
                          return (
                            <Card
                              key={list.id}
                              className="h-full flex gap-0 flex-col"
                            >
                              <CardHeader className="pb-3">
                                <CardTitle className="text-base sm:text-lg break-words">
                                  {highlightListName(listName)}
                                </CardTitle>
                                {listDescription && (
                                  <CardDescription className="break-words">
                                    {listDescription}
                                  </CardDescription>
                                )}
                              </CardHeader>
                              <CardContent className="flex-1 flex flex-col space-y-3">
                                <div className="space-y-1">
                                  <p className="text-sm font-semibold">
                                    Broj gostiju: {listGuests.length}
                                  </p>
                                  <div className="flex gap-3 text-xs sm:text-sm text-muted-foreground flex-wrap">
                                    <span>
                                      Potvrđenih:{" "}
                                      {
                                        listGuests.filter(({ guest: g }) => {
                                          const status =
                                            g.status ||
                                            (g.confirmed
                                              ? "Potvrđen"
                                              : "Na čekanju");
                                          return status === "Potvrđen";
                                        }).length
                                      }
                                    </span>
                                    <span>
                                      Na čekanju:{" "}
                                      {
                                        listGuests.filter(({ guest: g }) => {
                                          const status =
                                            g.status ||
                                            (g.confirmed
                                              ? "Potvrđen"
                                              : "Na čekanju");
                                          return status === "Na čekanju";
                                        }).length
                                      }
                                    </span>
                                    <span>
                                      Otkazanih:{" "}
                                      {
                                        listGuests.filter(({ guest: g }) => {
                                          const status =
                                            g.status ||
                                            (g.confirmed
                                              ? "Potvrđen"
                                              : "Na čekanju");
                                          return status === "Otkazan";
                                        }).length
                                      }
                                    </span>
                                  </div>
                                </div>
                                {listGuests.length > 0 && (
                                  <div className="space-y-1.5 max-h-96 overflow-y-auto flex-1">
                                    {listGuests.map(
                                      ({ guest, originalIndex }) => {
                                        const fullName = (
                                          guest.name || ""
                                        ).toString();
                                        const parts = fullName
                                          .trim()
                                          .split(/\s+/);
                                        const firstName =
                                          parts.length === 1
                                            ? parts[0] || ""
                                            : parts[0] || "";
                                        const lastName =
                                          parts.length > 1
                                            ? parts.slice(1).join(" ")
                                            : "";
                                        const displayName = lastName
                                          ? `${lastName} ${firstName}`.trim()
                                          : firstName;

                                        return (
                                          <div
                                            key={`${guest.name}-${originalIndex}`}
                                            className="text-sm p-2 rounded flex items-start justify-between gap-2 bg-muted"
                                          >
                                            <div className="flex-1 min-w-0">
                                              <p className="font-medium text-sm sm:text-base truncate">
                                                {displayName}
                                              </p>
                                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                {guest.phone && (
                                                  <span className="text-xs text-muted-foreground truncate">
                                                    {guest.phone}
                                                  </span>
                                                )}
                                                <span
                                                  className={`text-xs px-2 py-0.5 rounded ${
                                                    getStatusColors(
                                                      guest.status ||
                                                        (guest.confirmed
                                                          ? "Potvrđen"
                                                          : "Na čekanju")
                                                    ).badge
                                                  }`}
                                                >
                                                  {guest.status ||
                                                    (guest.confirmed
                                                      ? "Potvrđen"
                                                      : "Na čekanju")}
                                                </span>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-10 w-10 p-0 hover:bg-black/5 dark:hover:bg-white/10 dark:hover:text-accent-foreground"
                                                onClick={() =>
                                                  startEditingGuest(
                                                    originalIndex,
                                                    list.id
                                                  )
                                                }
                                              >
                                                <Edit className="h-3 w-3" />
                                              </Button>
                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                                                    onClick={() =>
                                                      setDeletingGuest({
                                                        listId: list.id,
                                                        guestIndex:
                                                          originalIndex,
                                                        guestName:
                                                          guest.name || "",
                                                      })
                                                    }
                                                  >
                                                    <Trash2 className="h-3 w-3" />
                                                  </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                  <AlertDialogHeader>
                                                    <AlertDialogTitle>
                                                      Da li ste sigurni?
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                      Ova akcija će obrisati
                                                      gosta "{guest.name}" iz
                                                      spiska. Ova akcija se ne
                                                      može poništiti.
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel>
                                                      Otkaži
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                      onClick={() =>
                                                        handleDeleteGuest(
                                                          list.id,
                                                          originalIndex
                                                        )
                                                      }
                                                      className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                    >
                                                      Obriši
                                                    </AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                                <div className="flex gap-2 pt-2 mt-auto items-center">
                                  <Button
                                    variant="default"
                                    className="flex-1 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                                    onClick={() => handleEditClick(list)}
                                  >
                                    <Edit className="mr-1 h-3 w-3" />
                                    <span className="inline">Izmjeni</span>
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        className="h-10 px-2 sm:px-3"
                                        onClick={() => setDeleteListId(list.id)}
                                      >
                                        <Trash2 className="h-3 w-3 sm:mr-1" />
                                        <span className="hidden sm:inline">
                                          Obriši
                                        </span>
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          Da li ste sigurni?
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Ova akcija će obrisati spisak "
                                          {listName}" i sve goste u njemu. Ova
                                          akcija se ne može poništiti.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel
                                          onClick={() => setDeleteListId(null)}
                                        >
                                          Otkaži
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() =>
                                            handleDeleteList(list.id)
                                          }
                                          className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Obriši
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Right: Desktop Sidebar with All Guests (>=1024px) */}
            <div className="hidden lg:block">
              <Card className="sticky top-20 max-h-[calc(100vh-8rem)] flex flex-col px-4 py-6">
                <CardHeader className="pb-0 border-b-0 px-0 pt-0">
                  <CardTitle className="text-base text-lg">
                    Svi gosti ({filteredGuests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden flex flex-col px-0 pb-0">
                  {/* Search Input */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                    <Input
                      placeholder="Pretraži goste po imenu..."
                      value={guestSearchQuery}
                      onChange={(e) => setGuestSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="mb-4">
                    <Label className="text-sm text-muted-foreground mb-2 block">
                      Filtriraj po statusu
                    </Label>
                    <Select
                      value={guestStatusFilter}
                      onValueChange={setGuestStatusFilter}
                    >
                      <SelectTrigger className="text-base md:text-sm">
                        <SelectValue placeholder="Svi statusi" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Svi">Svi</SelectItem>
                        <SelectItem value="Potvrđen">Potvrđen</SelectItem>
                        <SelectItem value="Na čekanju">Na čekanju</SelectItem>
                        <SelectItem value="Otkazan">Otkazan</SelectItem>
                        <SelectItem value="Bez stola">
                          Bez stola (nije na spisku)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Guests List */}
                  <div className="space-y-2 flex-1 overflow-y-auto">
                    {filteredGuests.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {guestSearchQuery || guestStatusFilter !== "Svi"
                          ? "Nema gostiju koji odgovaraju filterima"
                          : "Nema gostiju"}
                      </p>
                    ) : (
                      filteredGuests.map(
                        ({ guest, listId, listName, guestIndex }) => {
                          const fullName = (guest.name || "").toString();
                          const parts = fullName.trim().split(/\s+/);
                          const firstName =
                            parts.length === 1
                              ? parts[0] || ""
                              : parts[0] || "";
                          const lastName =
                            parts.length > 1 ? parts.slice(1).join(" ") : "";
                          const displayName = lastName
                            ? `${lastName} ${firstName}`.trim()
                            : firstName;

                          return (
                            <div
                              key={`${listId}-${guestIndex}`}
                              className="p-3 rounded-xl border bg-card text-card-foreground shadow-sm"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-base">
                                    {displayName}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <span className="text-xs text-muted-foreground">
                                      {listName}
                                    </span>
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded ${
                                        getStatusColors(
                                          guest.status ||
                                            (guest.confirmed
                                              ? "Potvrđen"
                                              : "Na čekanju")
                                        ).badge
                                      }`}
                                    >
                                      {guest.status ||
                                        (guest.confirmed
                                          ? "Potvrđen"
                                          : "Na čekanju")}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-10 w-10 p-0 hover:bg-black/5 dark:hover:bg-white/10 dark:hover:text-accent-foreground"
                                    onClick={() =>
                                      startEditingGuest(guestIndex, listId)
                                    }
                                  >
                                    <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                                        onClick={() =>
                                          setDeletingGuest({
                                            listId,
                                            guestIndex,
                                            guestName: guest.name || "",
                                          })
                                        }
                                      >
                                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          Potvrdite brisanje gosta
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Da li ste sigurni da želite da
                                          obrišete gosta "{guest.name}" iz
                                          spiska "{listName}
                                          "? Ova akcija se ne može poništiti.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>
                                          Otkaži
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() =>
                                            handleDeleteGuest(
                                              listId,
                                              guestIndex
                                            )
                                          }
                                          className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Obriši
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        <Dialog
          open={isCreateGuestDialogOpen}
          onOpenChange={(open) => {
            setIsCreateGuestDialogOpen(open);
            if (!open) {
              resetNewGuestForm();
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Kreiraj novog gosta</DialogTitle>
              <DialogDescription>
                Unesite podatke gosta. Spisak možete odabrati odmah ili kasnije
                — dok nije dodijeljen, gost je u „Bez stola” i vidi se u
                pogledu Svi gosti.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-guest-list">Spisak / sto (opciono)</Label>
                <Select
                  value={newGuestListId || "__none__"}
                  onValueChange={(v) => {
                    const id = v === "__none__" ? "" : v;
                    setNewGuestListId(id);
                    if (id) {
                      setNewGuest((g) =>
                        g.status === "Bez stola"
                          ? { ...g, status: "Na čekanju", confirmed: false }
                          : g
                      );
                    }
                  }}
                >
                  <SelectTrigger id="new-guest-list">
                    <SelectValue placeholder="Kasnije dodijeli spisku" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      Još nije na spisku (Bez stola)
                    </SelectItem>
                    {guestLists
                      .filter((list) => !isUnassignedGuestList(list))
                      .map((list) => (
                        <SelectItem key={list.id} value={String(list.id)}>
                          {list.attributes?.name ?? list.name ?? "Bez naziva"}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Ime *"
                  value={newGuestFirstName}
                  onChange={(e) => setNewGuestFirstName(e.target.value)}
                  onKeyDown={handleNewGuestFormKeyPress}
                />
                <Input
                  placeholder="Prezime"
                  value={newGuestLastName}
                  onChange={(e) => setNewGuestLastName(e.target.value)}
                  onKeyDown={handleNewGuestFormKeyPress}
                />
              </div>

              <Input
                placeholder="Telefon"
                value={newGuest.phone || ""}
                onChange={(e) => updateNewGuest("phone", e.target.value)}
                onKeyDown={handleNewGuestFormKeyPress}
              />

              <Textarea
                placeholder="Napomene"
                value={newGuest.notes || ""}
                onChange={(e) => updateNewGuest("notes", e.target.value)}
                rows={3}
              />

              <div className="space-y-2">
                <Label htmlFor="new-guest-status">Status dolaska</Label>
                <Select
                  value={
                    newGuest.status === "Bez stola" || !newGuest.status
                      ? "Na čekanju"
                      : newGuest.status
                  }
                  onValueChange={(value: string) =>
                    updateNewGuest(
                      "status",
                      value as NonNullable<Guest["status"]>
                    )
                  }
                >
                  <SelectTrigger id="new-guest-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Na čekanju">Na čekanju</SelectItem>
                    <SelectItem value="Potvrđen">Potvrđen</SelectItem>
                    <SelectItem value="Otkazan">Otkazan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateGuestDialogOpen(false);
                  resetNewGuestForm();
                }}
              >
                Otkaži
              </Button>
              <Button
                onClick={() => void handleCreateGuestToSelectedList()}
                disabled={!newGuestFirstName.trim()}
              >
                <Plus className="mr-2 h-4 w-4" />
                Dodaj gosta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Single "Novi spisak" button at the bottom */}
        <div className="flex justify-center pt-3">
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => resetForm()}
                variant="default"
                className="w-10 h-10 p-0"
                disabled={hasNoEvents}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto sm:max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Kreiraj novi spisak gostiju</DialogTitle>
                <DialogDescription>
                  Unesite informacije o spisku i dodajte goste
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="mb-2">
                    Naziv spiska *
                  </Label>
                  <Input
                    id="name"
                    value={listName}
                    onChange={(e) => {
                      setListName(e.target.value);
                      setListNameError(null);
                    }}
                    placeholder="npr. Porodica, Prijatelji, Kolege..."
                  />
                  {listNameError && (
                    <p className="text-destructive text-sm mt-1">
                      {listNameError}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="description" className="mb-2">
                    Opis
                  </Label>
                  <Textarea
                    id="description"
                    value={listDescription}
                    onChange={(e) => setListDescription(e.target.value)}
                    placeholder="Dodatne napomene o spisku..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Gosti</Label>

                  {/* Single guest form */}
                  <Card className="p-3 sm:p-4 mb-4 border-2 border-dashed">
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          placeholder="Ime *"
                          value={currentGuestFirstName}
                          onChange={(e) =>
                            setCurrentGuestFirstName(e.target.value)
                          }
                          onKeyDown={handleGuestFormKeyPress}
                          className="text-sm sm:text-base"
                        />
                        <Input
                          placeholder="Prezime"
                          value={currentGuestLastName}
                          onChange={(e) =>
                            setCurrentGuestLastName(e.target.value)
                          }
                          onKeyDown={handleGuestFormKeyPress}
                          className="text-sm sm:text-base"
                        />
                      </div>
                      <Input
                        placeholder="Telefon"
                        value={currentGuest.phone || ""}
                        onChange={(e) =>
                          updateCurrentGuest("phone", e.target.value)
                        }
                        onKeyDown={handleGuestFormKeyPress}
                        className="text-sm sm:text-base"
                      />
                      <Textarea
                        placeholder="Napomene"
                        value={currentGuest.notes || ""}
                        onChange={(e) =>
                          updateCurrentGuest("notes", e.target.value)
                        }
                        rows={2}
                        className="text-sm sm:text-base"
                      />
                      <div className="space-y-2">
                        <Label
                          htmlFor="status"
                          className="text-xs sm:text-sm font-normal"
                        >
                          Status dolaska
                        </Label>
                        <Select
                          value={currentGuest.status || "Na čekanju"}
                          onValueChange={(
                            value: "Potvrđen" | "Na čekanju" | "Otkazan"
                          ) => updateCurrentGuest("status", value)}
                        >
                          <SelectTrigger
                            id="status"
                            className="text-sm sm:text-base"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Na čekanju">
                              Na čekanju
                            </SelectItem>
                            <SelectItem value="Potvrđen">Potvrđen</SelectItem>
                            <SelectItem value="Otkazan">Otkazan</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        onClick={addGuestToList}
                        disabled={!currentGuestFirstName.trim()}
                        className="w-full text-sm sm:text-base"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Dodaj gosta u spisak
                      </Button>
                    </div>
                  </Card>

                  <DialogFooter className="flex-row gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsCreateDialogOpen(false);
                        resetForm();
                      }}
                    >
                      Otkaži
                    </Button>
                    <Button onClick={handleCreateList}>Kreiraj</Button>
                  </DialogFooter>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Izmjeni spisak gostiju</DialogTitle>
              <DialogDescription>
                Ažurirajte informacije o spisku i gostima
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name" className="mb-2">
                  Naziv spiska *
                </Label>
                <Input
                  id="edit-name"
                  value={listName}
                  onChange={(e) => {
                    setListName(e.target.value);
                    setListNameError(null);
                  }}
                  placeholder="npr. Porodica, Prijatelji, Kolege..."
                  className="mt-2"
                />
                {listNameError && (
                  <p className="text-destructive text-sm mt-1">
                    {listNameError}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="edit-description">Opis</Label>
                <Textarea
                  id="edit-description"
                  value={listDescription}
                  onChange={(e) => setListDescription(e.target.value)}
                  placeholder="Dodatne napomene o spisku..."
                  rows={3}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="mb-3 block">Gosti</Label>

                {/* Single guest form */}
                <Card className="p-3 sm:p-4 mb-4 border-2 border-dashed">
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder="Ime *"
                        value={currentGuestFirstName}
                        onChange={(e) =>
                          setCurrentGuestFirstName(e.target.value)
                        }
                        onKeyDown={handleGuestFormKeyPress}
                        className="text-sm sm:text-base"
                      />
                      <Input
                        placeholder="Prezime"
                        value={currentGuestLastName}
                        onChange={(e) =>
                          setCurrentGuestLastName(e.target.value)
                        }
                        onKeyDown={handleGuestFormKeyPress}
                        className="text-sm sm:text-base"
                      />
                    </div>
                    <Input
                      placeholder="Telefon"
                      value={currentGuest.phone || ""}
                      onChange={(e) =>
                        updateCurrentGuest("phone", e.target.value)
                      }
                      onKeyDown={handleGuestFormKeyPress}
                      className="text-sm sm:text-base"
                    />
                    <Textarea
                      placeholder="Napomene"
                      value={currentGuest.notes || ""}
                      onChange={(e) =>
                        updateCurrentGuest("notes", e.target.value)
                      }
                      rows={2}
                      className="text-sm sm:text-base"
                    />
                    <div className="space-y-2">
                      <Label
                        htmlFor="edit-status"
                        className="text-xs sm:text-sm font-normal"
                      >
                        Status dolaska
                      </Label>
                      <Select
                        value={
                          currentGuest.status ||
                          (currentGuest.confirmed
                            ? "Potvrđen"
                            : "Na čekanju") ||
                          "Na čekanju"
                        }
                        onValueChange={(
                          value: "Potvrđen" | "Na čekanju" | "Otkazan"
                        ) => updateCurrentGuest("status", value)}
                      >
                        <SelectTrigger
                          id="edit-status"
                          className="text-sm sm:text-base"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Na čekanju">Na čekanju</SelectItem>
                          <SelectItem value="Potvrđen">Potvrđen</SelectItem>
                          <SelectItem value="Otkazan">Otkazan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      onClick={addGuestToList}
                      disabled={!currentGuestFirstName.trim()}
                      className="w-full text-sm sm:text-base"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Dodaj gosta u spisak
                    </Button>
                  </div>
                </Card>
                <DialogFooter className="flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Otkaži
                  </Button>
                  <Button onClick={handleUpdateList}>Sačuvaj izmjene</Button>
                </DialogFooter>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Edit Guest Dialog */}
        <Dialog
          open={isEditGuestDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              cancelEditingGuest();
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Izmjeni gosta</DialogTitle>
              <DialogDescription>
                Ažurirajte informacije o gostu
              </DialogDescription>
            </DialogHeader>
            {editingGuest && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Ime i prezime *</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder="Ime *"
                      value={editingGuestFirstName}
                      onChange={(e) => setEditingGuestFirstName(e.target.value)}
                      className="mt-0 text-sm sm:text-base"
                    />
                    <Input
                      placeholder="Prezime"
                      value={editingGuestLastName}
                      onChange={(e) => setEditingGuestLastName(e.target.value)}
                      className="mt-0 text-sm sm:text-base"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-guest-phone">Telefon</Label>
                  <Input
                    id="edit-guest-phone"
                    value={editingGuest.phone || ""}
                    onChange={(e) =>
                      setEditingGuest({
                        ...editingGuest,
                        phone: e.target.value,
                      })
                    }
                    placeholder="Telefon"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-guest-notes">Napomene</Label>
                  <Textarea
                    id="edit-guest-notes"
                    value={editingGuest.notes || ""}
                    onChange={(e) =>
                      setEditingGuest({
                        ...editingGuest,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Napomene"
                    rows={3}
                    className="mt-2"
                  />
                </div>
                <div className="space-y-2 pt-2">
                  <Label
                    htmlFor="edit-guest-status"
                    className="text-xs sm:text-sm font-normal"
                  >
                    Status dolaska
                  </Label>
                  <Select
                    value={
                      editingGuest.status === "Bez stola" || !editingGuest.status
                        ? editingGuest.confirmed
                          ? "Potvrđen"
                          : "Na čekanju"
                        : editingGuest.status ||
                          (editingGuest.confirmed ? "Potvrđen" : "Na čekanju")
                    }
                    onValueChange={(value: string) => {
                      const st = value as Guest["status"];
                      setEditingGuest({
                        ...editingGuest,
                        status: st,
                        confirmed: st === "Potvrđen",
                      });
                    }}
                  >
                    <SelectTrigger
                      id="edit-guest-status"
                      className="text-sm sm:text-base"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Na čekanju">Na čekanju</SelectItem>
                      <SelectItem value="Potvrđen">Potvrđen</SelectItem>
                      <SelectItem value="Otkazan">Otkazan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!!editingList && (
                  <div className="space-y-2 pt-2">
                    <Label
                      htmlFor="edit-guest-list"
                      className="text-xs sm:text-sm font-normal"
                    >
                      Spisak
                    </Label>
                    <Select
                      value={String(
                        editingGuestTargetListId ?? editingList.id ?? ""
                      )}
                      onValueChange={(value) =>
                        setEditingGuestTargetListId(Number(value))
                      }
                    >
                      <SelectTrigger
                        id="edit-guest-list"
                        className="text-sm sm:text-base"
                      >
                        <SelectValue placeholder="Odaberite spisak" />
                      </SelectTrigger>
                      <SelectContent>
                        {guestLists.map((list) => (
                          <SelectItem key={list.id} value={String(list.id)}>
                            {isUnassignedGuestList(list)
                              ? "Bez stola (nije na spisku)"
                              : list.attributes?.name ??
                                list.name ??
                                "Bez naziva"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex-row justify-end gap-2">
              <Button variant="outline" onClick={cancelEditingGuest}>
                Otkaži
              </Button>
              <Button
                onClick={() => void saveEditedGuest()}
                disabled={!editingGuestFirstName.trim()}
              >
                <Save className="mr-2 h-4 w-4" />
                Sačuvaj izmjene
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Reorder lists dialog */}
        <Dialog
          open={isReorderDialogOpen}
          onOpenChange={setIsReorderDialogOpen}
        >
          <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col overflow-hidden gap-0 px-4 py-6 sm:p-6">
            <div className="flex flex-col max-h-[calc(80vh-3rem)] overflow-hidden min-h-0">
              <DialogHeader className="shrink-0 pb-4">
                <DialogTitle>Redosljed spiskova</DialogTitle>
                <DialogDescription>
                  Prevucite spiskove da podesite redosled prikaza i izvoza u
                  PDF.
                </DialogDescription>
              </DialogHeader>
              <div className="overflow-y-auto min-h-0 flex-1 -mx-4 sm:mx-0">
                <Reorder.Group
                  axis="y"
                  values={listsForReorder}
                  onReorder={handleReorderLists}
                  className="space-y-2 pl-4 pr-4 sm:px-0"
                >
                  {listsForReorder.map((list) => (
                    <Reorder.Item
                      key={list.id}
                      value={list}
                      className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 cursor-grab active:cursor-grabbing"
                    >
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {list.attributes?.name ?? list.name ?? "Bez naziva"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(() => {
                            const g =
                              list.attributes?.guest ??
                              list.attributes?.guests ??
                              list.guests ??
                              [];
                            const arr = Array.isArray(g) ? g : [];
                            return `${arr.length} gost${
                              arr.length === 1
                                ? ""
                                : arr.length >= 2 && arr.length <= 4
                                ? "a"
                                : "iju"
                            }`;
                          })()}
                        </p>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              </div>
              <DialogFooter className="shrink-0 pt-4 border-t mt-4">
                <Button onClick={() => setIsReorderDialogOpen(false)}>
                  Sačuvaj redosljed
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
